import { useState } from "react";
import { toast } from "sonner";
import { Users2, Upload, X, Volume2, Plus, Edit2 } from "lucide-react";
import {
  adminCreateGroup, adminUpdateGroup, adminDeleteGroup,
  adminUploadGroupAudio, adminClearGroupAudio,
} from "@/lib/apiClient";
import AdminAudioRecorderButton from "./AdminAudioRecorderButton";

/**
 * Learn-by-Group builder — admin selects an arbitrary set of items into a
 * group (independent of the item.rule section tags used by Test mode), then
 * attaches one audio file (upload or live-recorded) that plays for the
 * whole group.
 */
export default function GroupsEditor({ m, code, onChange }) {
  const groups = m.groups || [];
  const items = m.items || [];
  const [building, setBuilding] = useState(null); // { id?, label, item_indices: Set } or null

  const startNew = () => setBuilding({ label: "", item_indices: new Set() });
  const startEdit = (g) => setBuilding({ id: g.id, label: g.label || "", item_indices: new Set(g.item_indices || []) });
  const cancelBuild = () => setBuilding(null);

  const toggleIndex = (i) => {
    setBuilding((b) => {
      const next = new Set(b.item_indices);
      if (next.has(i)) next.delete(i); else next.add(i);
      return { ...b, item_indices: next };
    });
  };

  const saveBuild = async () => {
    const item_indices = Array.from(building.item_indices).sort((a, b) => a - b);
    if (item_indices.length === 0) return toast.error("Select at least one item");
    try {
      const payload = { label: building.label, item_indices };
      const saved = building.id
        ? await adminUpdateGroup(code, building.id, payload)
        : await adminCreateGroup(code, payload);
      const next = building.id
        ? groups.map((g) => (g.id === building.id ? { ...g, ...saved } : g))
        : [...groups, saved];
      onChange(next);
      setBuilding(null);
      toast.success(building.id ? "Group updated" : "Group created");
    } catch (e) { toast.error(e?.response?.data?.detail || "Save failed"); }
  };

  const deleteGroup = async (g) => {
    if (!window.confirm(`Delete group "${g.label || g.id}"?`)) return;
    try {
      await adminDeleteGroup(code, g.id);
      onChange(groups.filter((x) => x.id !== g.id));
      toast.success("Deleted");
    } catch (e) { toast.error(e?.response?.data?.detail || "Delete failed"); }
  };

  // Mirrors the full raw field set (narrated_audio_id/mime, not just the
  // display URL) into local state — the upload/clear endpoints persist the
  // id server-side, and a partial local patch here would be silently wiped
  // the next time the top-level Save button PUTs this whole `groups` array
  // back to the server (which replaces it wholesale).
  const uploadAudio = async (g, file) => {
    try {
      const r = await adminUploadGroupAudio(code, g.id, file);
      onChange(groups.map((x) => (x.id === g.id
        ? { ...x, narrated_audio_id: r.narrated_audio_id, narrated_audio_mime: r.narrated_audio_mime, narrated_audio_url: r.audio_url }
        : x)));
      toast.success("Group audio uploaded");
    } catch (e) { toast.error(e?.response?.data?.detail || "Upload failed"); }
  };

  const clearAudio = async (g) => {
    if (!window.confirm("Remove this group's audio?")) return;
    try {
      await adminClearGroupAudio(code, g.id);
      onChange(groups.map((x) => {
        if (x.id !== g.id) return x;
        const { narrated_audio_id, narrated_audio_mime, narrated_audio_url, ...rest } = x;
        return rest;
      }));
      toast.success("Cleared");
    } catch (e) { toast.error(e?.response?.data?.detail || "Failed"); }
  };

  // Shared builder form — rendered either at the top (creating a new group)
  // or inline under the row being edited (accordion-style, see below).
  const renderBuilder = () => (
    <div className="rounded-xl border border-[#FED7AA] bg-[#FFF7ED] p-4" data-testid="group-builder">
      <div className="text-sm font-medium text-[#1F2937] mb-2">{building.id ? "Edit group" : "New group"}</div>
      <input
        data-testid="group-label-input"
        value={building.label}
        onChange={(e) => setBuilding((b) => ({ ...b, label: e.target.value }))}
        placeholder="Group label (e.g. Sukoon pair 1)"
        className="w-full px-3 py-2 rounded-xl bg-white border border-[#E7E0D2] focus:border-[#064E3B] outline-none text-sm mb-3"
      />
      <div className="text-xs uppercase tracking-[0.16em] text-[#4B5563] mb-2">Select member items</div>
      <div className="flex flex-wrap gap-2 max-h-48 overflow-y-auto">
        {items.map((it, i) => (
          <label key={i} className={`inline-flex items-center gap-1.5 px-2.5 py-1.5 rounded-lg border text-sm cursor-pointer ${
            building.item_indices.has(i) ? "bg-[#064E3B] text-white border-[#064E3B]" : "bg-white border-[#E7E0D2]"
          }`}>
            <input type="checkbox" checked={building.item_indices.has(i)} onChange={() => toggleIndex(i)} className="hidden" />
            <span className="font-arabic" dir="rtl">{it.ar}</span>
          </label>
        ))}
      </div>
      <div className="mt-3 flex items-center gap-2">
        <button data-testid="group-save-btn" onClick={saveBuild}
          className="text-xs px-3 py-1.5 rounded-full bg-[#064E3B] text-white hover:bg-[#047857]">Save</button>
        <button onClick={cancelBuild} className="text-xs px-3 py-1.5 rounded-full border border-[#E7E0D2] hover:border-[#064E3B] text-[#1F2937]">Cancel</button>
      </div>
    </div>
  );

  return (
    <div className="space-y-4" data-testid="groups-editor">
      <div className="flex items-center justify-between">
        <div className="text-sm text-[#4B5563]">{groups.length} groups · used by the student "Learn by Group" screen</div>
        {!building && (
          <button data-testid="add-group-btn" onClick={startNew}
            className="inline-flex items-center gap-1.5 text-xs px-3 py-1.5 rounded-full bg-[#064E3B] text-white hover:bg-[#047857]">
            <Plus className="w-3.5 h-3.5" /> New group
          </button>
        )}
      </div>

      {/* New-group builder — no existing row to nest under, so it stays here. */}
      {building && !building.id && renderBuilder()}

      <div className="space-y-2">
        {groups.map((g) => (
          <div key={g.id} data-testid={`group-row-${g.id}`}>
            {building?.id === g.id ? (
              // Edit accordion — expands in place under this row instead of
              // jumping to the top of the list.
              renderBuilder()
            ) : (
              <div className="rounded-xl border border-[#E7E0D2] bg-white p-4">
                <div className="flex items-start justify-between gap-3 flex-wrap">
                  <div className="flex items-center gap-2">
                    <Users2 className="w-4 h-4 text-[#D97706]" />
                    <div>
                      <div className="text-sm font-medium text-[#1F2937]">{g.label || `(untitled — ${g.id})`}</div>
                      <div className="text-xs text-[#4B5563]">{(g.item_indices || []).length} items</div>
                    </div>
                  </div>
                  <div className="flex items-center gap-2">
                    <button onClick={() => startEdit(g)} disabled={!!building} className="inline-flex items-center gap-1 text-xs px-2.5 py-1 rounded-full border border-[#E7E0D2] hover:border-[#064E3B] text-[#1F2937] disabled:opacity-40 disabled:cursor-not-allowed">
                      <Edit2 className="w-3 h-3" /> Edit
                    </button>
                    <button onClick={() => deleteGroup(g)} disabled={!!building} className="inline-flex items-center gap-1 text-xs px-2.5 py-1 rounded-full border border-[#FECACA] text-[#7F1D1D] hover:bg-[#FEF2F2] disabled:opacity-40 disabled:cursor-not-allowed">
                      <X className="w-3 h-3" /> Delete
                    </button>
                  </div>
                </div>
                <div className="mt-3 pt-3 border-t border-[#E7E0D2] flex flex-wrap items-center gap-2">
                  {g.narrated_audio_url ? (
                    <>
                      <a href={`${process.env.REACT_APP_BACKEND_URL}${g.narrated_audio_url}`} target="_blank" rel="noreferrer"
                        className="inline-flex items-center gap-1 text-xs text-[#064E3B] hover:underline"><Volume2 className="w-3 h-3" /> Preview audio</a>
                      <button onClick={() => clearAudio(g)} className="text-[#9CA3AF] hover:text-[#EF4444]" title="Remove audio"><X className="w-3.5 h-3.5" /></button>
                    </>
                  ) : (
                    <>
                      <label className="inline-flex items-center gap-1.5 text-xs px-3 py-1.5 rounded-full border border-[#E7E0D2] hover:border-[#064E3B] text-[#1F2937] cursor-pointer">
                        <Upload className="w-3.5 h-3.5" /> Upload from desktop
                        <input type="file" accept="audio/*" hidden onChange={(e) => e.target.files?.[0] && uploadAudio(g, e.target.files[0])} />
                      </label>
                      <AdminAudioRecorderButton filename={`${code}-${g.id}.webm`} onRecorded={(file) => uploadAudio(g, file)} />
                    </>
                  )}
                </div>
              </div>
            )}
          </div>
        ))}
        {groups.length === 0 && !building && (
          <div className="text-center text-sm text-[#4B5563] py-6">No groups yet — click "New group" to create one.</div>
        )}
      </div>
    </div>
  );
}
